import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { ROUTES } from '@/config/routes'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Background from '../../components/background'

interface ErrorInfo {
  error?: string
  error_code?: string
  error_description?: string
}

export function AuthErrorPage() {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null)

  useEffect(() => {
    const storedError = sessionStorage.getItem('interceptedAuthError')
    if (storedError) {
      try {
        const parsedError = JSON.parse(storedError)
        setErrorInfo(parsedError)
      } catch (e) {
        console.error('Failed to parse intercepted auth error:', e)
        setErrorInfo({
          error: 'Invalid Error Format',
          error_description: 'Could not parse the error information from storage.',
        })
      }
      sessionStorage.removeItem('interceptedAuthError')
    } else {
      setErrorInfo({
        error: 'No Error Found',
        error_description: 'No error information was found. You may have landed on this page by mistake.',
      })
    }
  }, [])

  function getErrorMessage(authError: ErrorInfo): {
    title: string
    description: string
    isInviteError: boolean
  } {
    const { error, error_code, error_description } = authError

    // Handle invite-specific errors
    if (error === 'access_denied' && error_code === 'otp_expired') {
      return {
        title: 'Invite Link Expired',
        description: 'This invitation link has expired. Please request a new invitation from your administrator.',
        isInviteError: true,
      }
    }

    if (error === 'access_denied' && error_description?.includes('invite')) {
      return {
        title: 'Invalid Invite Link',
        description:
          'This invitation link is invalid or has already been used. Please request a new invitation from your administrator.',
        isInviteError: true,
      }
    }

    // Handle password reset errors
    if (error === 'access_denied' && error_code === 'otp_expired' && error_description?.includes('Email link')) {
      return {
        title: 'Reset Link Expired',
        description: 'This password reset link has expired. Please request a new password reset.',
        isInviteError: false,
      }
    }

    // Handle generic access denied
    if (error === 'access_denied') {
      return {
        title: 'Access Denied',
        description:
          error_description ||
          'Access to this resource has been denied. Please contact your administrator for assistance.',
        isInviteError: false,
      }
    }

    // Handle invalid token errors
    if (error === 'invalid_token') {
      return {
        title: 'Invalid Link',
        description: 'This authentication link is invalid. Please request a new link.',
        isInviteError: false,
      }
    }

    // Generic error fallback
    return {
      title: 'Authentication Error',
      description:
        error_description || `An authentication error occurred: ${error}. Please try again or contact support.`,
      isInviteError: false,
    }
  }

  if (!errorInfo) {
    return (
      <Background>
        <div className="flex min-h-screen items-center justify-center">Loading...</div>
      </Background>
    )
  }

  return (
    <Background>
      {errorInfo && (
        <div className="flex min-h-screen items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="capitalize">{getErrorMessage(errorInfo).title}</CardTitle>
              <CardDescription>{getErrorMessage(errorInfo).description}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      )}
    </Background>
  )
}
